/* shield-tools.js — shieldperception.com
 *
 * Restores the two differential-turnover calculators. Written 20 September 2026,
 * after Shopify was confirmed to strip <script> out of Custom Liquid sections
 * entirely — not escaping it, removing it. The markup and the <style> block on
 * both pages survive the paste; the script does not, so the inputs render and
 * nothing computes.
 *
 * This file lives in the theme, where nothing sanitizes it. It does nothing at
 * all unless it finds the elements it belongs to, so loading it site-wide from
 * theme.liquid is safe on every page.
 *
 *   Homepage hero  : #h-hc #h-lo #h-hi      -> #h-total #h-three
 *   Calculator page: #headcount #salary #baseline #elevated #share #mult
 *                    -> #o-group #o-gap #o-excess #o-each #o-total #o-note #o-three
 *
 * ONE MODEL, TWO SURFACES. Both read the same function. The hero holds salary,
 * share and multiplier at the page's stated defaults ($110,000 / 25% / 2x) and
 * exposes the three inputs that come straight out of an HRIS. The calculator
 * page exposes all six. At the shared defaults both return $5.5M a year and
 * $16.5M over three years, which is the figure the registry says public work
 * uses and the figure the LinkedIn About section carries. Do not let them drift.
 */
(function () {
  "use strict";

  /* ---------------------------------------------------------------- model */

  /* Excess departures are counted against the ELEVATED GROUP ONLY, not the
     whole headcount. This is the calculator version of differential turnover
     and it is deliberately not the book version, which counts the excess
     against total headcount and lands an order of magnitude higher. The two
     are not interchangeable. See the figure registry in CLAUDE.md. */
  function model(hc, salary, baseline, elevated, sharePct, mult) {
    var group = hc * (sharePct / 100);
    var gap = elevated - baseline;               // percentage points
    var excess = group * (gap / 100);            // departures a year above the floor
    var each = salary * mult;                    // replacement cost per departure
    var annual = excess * each;
    return {
      group: group,
      gap: gap,
      excess: excess,
      each: each,
      annual: annual,
      three: annual * 3,
      valid: hc > 0 && salary > 0 && mult > 0 && sharePct > 0 && gap > 0
    };
  }

  /* ----------------------------------------------------------- formatting */

  function money(n) {
    if (!isFinite(n) || n <= 0) return "$0";
    if (n >= 1e6) {
      var m = n / 1e6;
      /* One decimal where it carries information, none where it is noise.
         $5.5M keeps its half; $11M does not become $11.0M. */
      var s = m >= 100 ? String(Math.round(m)) : m.toFixed(1).replace(/\.0$/, "");
      return "$" + s + "M";
    }
    if (n >= 1e3) return "$" + Math.round(n / 1e3) + "K";
    return "$" + Math.round(n);
  }

  function dollars(n) {
    return "$" + Math.round(n).toLocaleString("en-US");
  }

  function count(n) {
    if (!isFinite(n)) return "—";
    return n >= 10 ? Math.round(n).toLocaleString("en-US")
                   : (Math.round(n * 10) / 10).toString();
  }

  function points(n) {
    var v = Math.round(n * 10) / 10;
    return v + (v === 1 ? " point" : " points");
  }

  function num(el, fallback) {
    if (!el) return fallback;
    var v = parseFloat(el.value);
    return isFinite(v) ? v : fallback;
  }

  function set(id, text) {
    var el = document.getElementById(id);
    if (el) el.textContent = text;
  }

  var DASH = "—";

  /* ------------------------------------------------------- homepage hero */

  function hero() {
    var hc = document.getElementById("h-hc");
    var lo = document.getElementById("h-lo");
    var hi = document.getElementById("h-hi");
    if (!hc || !lo || !hi) return false;

    /* Held at the defaults the hero's own assumption line states. If that
       copy is ever edited, edit these to match in the same pass. */
    var SALARY = 110000, SHARE = 25, MULT = 2;

    function run() {
      var r = model(num(hc, 0), SALARY, num(lo, 0), num(hi, 0), SHARE, MULT);
      if (!r.valid) {
        set("h-total", DASH);
        set("h-three", DASH);
        return;
      }
      set("h-total", money(r.annual));
      set("h-three", money(r.three));
    }

    [hc, lo, hi].forEach(function (el) {
      el.addEventListener("input", run);
      el.addEventListener("change", run);
    });
    run();
    return true;
  }

  /* ---------------------------------------------------- calculator page */

  function full() {
    var f = {
      hc: document.getElementById("headcount"),
      salary: document.getElementById("salary"),
      baseline: document.getElementById("baseline"),
      elevated: document.getElementById("elevated"),
      share: document.getElementById("share"),
      mult: document.getElementById("mult")
    };
    if (!f.hc || !f.salary || !f.baseline || !f.elevated || !f.share || !f.mult) return false;

    function run() {
      var r = model(
        num(f.hc, 0), num(f.salary, 0), num(f.baseline, 0),
        num(f.elevated, 0), num(f.share, 0), num(f.mult, 0)
      );

      if (!r.valid) {
        ["o-group", "o-gap", "o-excess", "o-each", "o-total", "o-three"]
          .forEach(function (id) { set(id, DASH); });
        set("o-note", r.gap <= 0 && num(f.elevated, 0) > 0
          ? "The highest group has to leave faster than the lowest group for there to be a gap to price."
          : "");
        return;
      }

      set("o-group", count(r.group));
      set("o-gap", points(r.gap));
      set("o-excess", count(r.excess));
      set("o-each", dollars(r.each));
      set("o-total", money(r.annual));
      set("o-three", money(r.three));

      /* The hedge the standards require, carried in the sentence rather than
         in a footnote, and rebuilt from the reader's own inputs. */
      set("o-note",
        count(r.excess) + " departures a year above your floor, at " +
        dollars(r.each) + " each. The replacement multiplier is an assumption " +
        "built from the published SHRM and Gallup bands, not a measurement of " +
        "your company. Change it and this number changes with it.");
    }

    Object.keys(f).forEach(function (k) {
      f[k].addEventListener("input", run);
      f[k].addEventListener("change", run);
    });
    run();
    return true;
  }

  /* ------------------------------------------------------------- startup */

  function init() { hero(); full(); }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  /* Horizon swaps sections in without a full page load, so a section re-render
     replaces the inputs and drops the listeners. Re-bind when that happens. */
  document.addEventListener("shopify:section:load", init);
  document.addEventListener("shopify:section:select", init);
})();